import axios from "axios";
import { MAIN_API, SSENSE_URL } from "./stepoutAPI";
import { decryptResponse } from "../utils/decryptData";
import apiClient from "../utils/axiosClient";

// export const getAllMatchesByAnalystId = async ({
//   page = 0,
//   analyst_id,
//   token,
//   searchMatchId,
//   searchTournament,
//   searchTeam,
//   searchByAnalyzed,
//   startDate,
//   endDate,
// }) => {
//   try {
//     let url = `${MAIN_API}get_matches_by_analyst_id/?offset=${page}&limit=20`;

//     const response = await axios({
//       method: "POST",
//       url,
//       data: {
//         analyst_id: analyst_id,
//         match_id: searchMatchId,
//         team_name: searchTeam,
//         tournament_name: searchTournament,
//         match_status: searchByAnalyzed,
//         // match_status: [1, 2, 4, 5, 3],
//         start_date: startDate,
//         end_date: endDate,
//       },
//       headers: {
//         Authorization: "ANALYST " + token,
//         "Content-Type": "application/json",
//       },
//     });
//     // const response_data = decryptResponse(response?.data?.response_data, true);
//     const response_data = response?.data?.response_data;
//     return response_data;
//   } catch (error) {
//     throw error;
//   }
// };

export const getAllMatchesByAnalystId = async ({
  page = 0,
  analyst_id,
  token,
  searchMatchId,
  searchTournament,
  searchTeam,
  searchByAnalyzed,
  startDate,
  endDate,
}) => {
  try {
    const response = await apiClient.post(
      "get_matches_by_analyst_id/",
      {
        analyst_id,
        match_id: searchMatchId,
        team_name: searchTeam,
        tournament_name: searchTournament,
        match_status: searchByAnalyzed,
        start_date: startDate,
        end_date: endDate,
      },
      {
        params: {
          offset: page,
          limit: 20,
          hashed_required: true,
        },
        headers: {
          Authorization: `ANALYST ${token}`,
        },
        timeout: 30000,
        'axios-retry': {
          retries: 0,
        },
      }
    );

    return decryptResponse(response?.data?.response_data, true);
  } catch (error) {
    throw error;
  }
};
export const getReviewerFeedbackStrings = async ({
  matchId,
  token,
  reviewerId,
}) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `${SSENSE_URL}get_reviewer_feedback/?match_id=${matchId}&reviewer_id=${reviewerId}`,
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    });
    return data?.response_data;
  } catch (error) {
    throw error;
  }
};

export const getReviewerFeedbackHistory = async ({ matchId, token }) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `${MAIN_API}get_review_feedback_history/`,
      params: { match_id: matchId },
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    });
    return data?.response_data;
  } catch (error) {
    throw error;
  }
};

// Ids of every reviewer on a match; falls back to `fallbackId` when none are
// assigned (previous behaviour, which queried with the analyst's own id).
export const matchReviewerIds = (matchDetails, fallbackId) => {
  const ids = (matchDetails?.reviewer_details ?? []).map((r) => r.id).filter((id) => id != null);
  return ids.length > 0 ? ids : fallbackId != null ? [fallbackId] : [];
};

// Open errors first, so a string any reviewer still flags shows as an error.
const REVIEW_STATUS_PRIORITY = [0, 3, 1, 2];
const _reviewRank = (status) => {
  const i = REVIEW_STATUS_PRIORITY.indexOf(status);
  return i === -1 ? REVIEW_STATUS_PRIORITY.length : i;
};

/**
 * Feedback from every reviewer of a match, one request per reviewer, merged
 * by string.  Each merged entry shows the highest-priority status and keeps
 * the per-reviewer rows in `byReviewer` ({...row, reviewer_id}) so updates go
 * back to the reviewer(s) who raised them.
 *
 * @returns {Promise<{string_details: Object<string, object>,
 *                    reviewers: Array<{reviewerId, failed: boolean, total: number, openErrors: number}>}>}
 */
export const getAllReviewerFeedbackStrings = async ({ matchId, token, reviewerIds }) => {
  // One reviewer's request failing must not hide the others' feedback: a failed
  // reviewer counts as no data and is marked `failed` in `reviewers`.  Only
  // when every request fails does this throw, so the query reports an error.
  const settled = await Promise.allSettled(
    reviewerIds.map((reviewerId) => getReviewerFeedbackStrings({ matchId, token, reviewerId }))
  );
  if (settled.length > 0 && settled.every((r) => r.status === "rejected")) {
    throw settled[0].reason;
  }
  const responses = settled.map((r, i) => ({
    reviewerId: reviewerIds[i],
    data: r.status === "fulfilled" ? r.value : null,
    failed: r.status === "rejected",
  }));

  const rowsByString = new Map();
  responses.forEach(({ reviewerId, data }) => {
    Object.entries(data?.string_details ?? {}).forEach(([string, row]) => {
      const key = string.trim();
      if (!rowsByString.has(key)) rowsByString.set(key, []);
      rowsByString.get(key).push({ ...row, reviewer_id: reviewerId });
    });
  });

  const string_details = {};
  rowsByString.forEach((rows, key) => {
    const top = rows.reduce((a, b) => (_reviewRank(b.string_status) < _reviewRank(a.string_status) ? b : a));
    const comments = rows
      .filter((r) => r.string_status === top.string_status && r.comments)
      .map((r) => r.comments)
      .join(" | ");
    string_details[key] = { ...top, comments, byReviewer: rows };
  });

  // Per-reviewer counts, so screens can show who has given feedback.
  const reviewers = responses.map(({ reviewerId, data, failed }) => {
    const rows = Object.values(data?.string_details ?? {});
    return {
      reviewerId,
      failed,
      total: rows.length,
      openErrors: rows.filter((r) => r.string_status === 0).length,
    };
  });
  return { string_details, reviewers };
};

/**
 * The reviewer rows a TechQC action on `item` must update: its rows still
 * marked as errors (status 0), or every row with `all`.  Items with no
 * reviewer rows (e.g. a QC-only error) go to `fallbackReviewerId`.
 */
export const reviewRowsFor = (item, fallbackReviewerId, { all = false } = {}) => {
  const rows = (item?.reviewEntries ?? []).filter((r) => all || r.string_status === 0);
  if (rows.length > 0) return rows;
  return [{ reviewer_id: fallbackReviewerId, string_status: item?.reviewStatus, comments: item?.reviewComments }];
};

export const saveReviewerFeedback = async ({ token, body }) => {
  try {
    const { data } = await axios.post(
      `${SSENSE_URL}save_reviewer_feedback/`,
      body,
      {
        headers: {
          Authorization: `ANALYST ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Combined Sense vector_map for a match (all given players).
 * @param {object} p
 * @param {string|number} p.matchId
 * @param {Array<number>} p.userIds  bulk_user_id
 * @param {string} p.token
 * @returns {Promise<object>} the sense payload (contains vector_map)
 */
export const getCombinedSenseData = async ({ matchId, userIds, organizerId, token }) => {
  const { data } = await axios.post(
    `${SSENSE_URL}get_combined_sense_data_of_user_by_match/`,
    {
      bulk_user_id: userIds,
      match_id: String(matchId),
      ...(organizerId ? { organizer_id: organizerId } : {}),
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return data?.response_data ?? data?.data ?? data ?? null;
};

// ─── Match Lineup ──────────────────────────────────────────────────────────

/**
 * Fetch the player list for one team in a match.
 * Response is AES-encrypted ({ response_message, response_data }) → decrypted
 * to an array of player objects (see Match Lineup spec §1).
 *
 * @param {object} p
 * @param {string|number} p.teamId
 * @param {string|number} p.matchId
 * @param {string|number} p.organizerId
 * @param {boolean} p.playedInMatch  true when match status ∈ {3,6,7,8,9}
 * @param {string} p.token
 * @returns {Promise<Array>}
 */
export const getPlayersRelatedToTeam = async ({
  teamId,
  matchId,
  organizerId,
  playedInMatch = false,
  token,
}) => {
  const { data } = await axios({
    method: "GET",
    url: `${MAIN_API}playersRelatedToTeam/${teamId}`,
    params: {
      match_id: matchId,
      // organizer_id is optional — only sent when present (backend resolves
      // from the token otherwise).
      ...(organizerId ? { organizer_id: organizerId } : {}),
      played_in_match: false,// playedInMatch,
      hashed_required: true,
    },
    headers: {
      Authorization: "ANALYST " + token,
      "Content-Type": "application/json",
    },
  });
  // { response_message, response_data: <decrypted array> }
  return decryptResponse(data, false)?.response_data ?? [];
};

/**
 * Create a brand-new player (used when global search finds no match).
 * Encrypted response → returns the decrypted object (contains user_id).
 *
 * @param {object} p
 * @param {object} p.form  { firstName, lastName, email, phone, countryCode,
 *   position, preferredFoot, dob (YYYY-MM-DD), height, weight, isDummy }
 * @param {string|number} p.teamId
 * @param {string|number} [p.organizerId]
 * @param {string} p.token
 */
export const createUser = async ({ form, teamId, organizerId, token }) => {
  const fd = new FormData();
  fd.append("email", form.email || "");
  fd.append("mobile", form.phone || "");
  fd.append("country_code", form.countryCode || "");
  if (organizerId) fd.append("organizer_id", organizerId);
  fd.append("first_name", form.firstName || "");
  fd.append("last_name", form.lastName || "");
  fd.append("preferred_position", form.position || 0);
  fd.append("team_id", teamId || 0);
  fd.append("hashed_required", true);
  // category 1 = UserCategory.ACTUAL (spec §8); 0 = dummy player.
  fd.append("category", form.isDummy ? 0 : 1);
  fd.append("height", form.height || 0);
  fd.append("weight", form.weight || 0);
  fd.append("preferred_foot", form.preferredFoot || 0);
  // Backend expects MM/DD/YY
  fd.append(
    "dob",
    form.dob
      ? (() => {
          const d = new Date(form.dob);
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const yy = String(d.getFullYear()).slice(-2);
          return `${mm}/${dd}/${yy}`;
        })()
      : ""
  );

  try {
    const { data } = await axios.post(`${MAIN_API}createUser/`, fd, {
      headers: { Authorization: "ANALYST " + token },
    });
    const parsed = readResponseData(data);
    return { user_id: parsed?.user_id ?? parsed?.id ?? null, raw: parsed };
  } catch (err) {
    const env = err?.response?.data;
    // Spec §8.1 — creation re-validates duplicates server-side and can block.
    if (env?.response_data) throw duplicateError(env, err?.response?.status);
    throw err;
  }
};

// ─── Duplicate player flow (spec §2 – §14) ────────────────────────────────

/**
 * `hashed_required` endpoints answer with an AES-encrypted base64 string, but
 * the duplicate-flow spec documents plain JSON for the same routes. Read
 * either shape rather than assuming one.
 */
const readResponseData = (data) => {
  const rd = data?.response_data;
  if (typeof rd !== "string") return rd;
  try {
    return decryptResponse(rd, true);
  } catch (e) {
    return rd;
  }
};

export const DUPLICATE_TYPE = {
  NONE: "NO_DUPLICATE",
  SAME_ORGANIZER_FAMILY: "SAME_ORGANIZER_FAMILY",
  SAME_COUNTRY: "SAME_COUNTRY",
  DIFFERENT_COUNTRY: "DIFFERENT_COUNTRY",
};

/** Duplicate types that must never block creation (spec §3.1 / §3.2). */
export const isCreatableDuplicate = (duplicateType) =>
  duplicateType === DUPLICATE_TYPE.NONE ||
  duplicateType === DUPLICATE_TYPE.DIFFERENT_COUNTRY ||
  duplicateType == null;

/**
 * Normalise every duplicate-bearing envelope into one shape. Works for HTTP 200
 * and HTTP 400 alike — matched rows are returned in both cases (spec §4, §18).
 */
const normalizeDuplicateEnvelope = (envelope, httpStatus) => {
  const rd = envelope?.response_data ?? {};
  return {
    duplicateType: rd.duplicate_type ?? DUPLICATE_TYPE.NONE,
    // Spec §5 (too-many-matches) 400s without a `blocked` flag.
    blocked: rd.blocked ?? httpStatus === 400,
    message: rd.message ?? "",
    data: Array.isArray(rd.data) ? rd.data : [],
    count: rd.count ?? null,
    next: rd.next ?? null,
    prev: rd.prev ?? null,
    status: httpStatus ?? 200,
  };
};

/** Error carrying the duplicate payload returned alongside a 400. */
const duplicateError = (envelope, httpStatus) => {
  const rd = envelope?.response_data ?? {};
  const e = new Error(
    rd.message || envelope?.response_message || "Duplicate player found"
  );
  e.duplicate = normalizeDuplicateEnvelope(envelope, httpStatus);
  e.blocked = e.duplicate.blocked;
  return e;
};

/** Minimal payload the approval/flag endpoints expect (spec §9 / §10). */
export const toPotentialDuplicates = (rows = []) =>
  rows.map((p) => ({ id: p.id, fuzz_ratio: p.fuzz_ratio }));

/**
 * Global player search (spec §3).
 * Never throws on a duplicate — the caller inspects duplicateType/blocked/data.
 * @returns {Promise<{duplicateType,blocked,message,data,count,next,prev,status}>}
 */
export const searchPlayerGlobal = async ({ name, organizerId, limit, token }) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `${MAIN_API}search_player_global/`,
      params: {
        name,
        ...(organizerId ? { organizer_id: organizerId } : {}),
        ...(limit ? { limit } : {}),
      },
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    });
    return normalizeDuplicateEnvelope(data, 200);
  } catch (err) {
    const env = err?.response?.data;
    if (env?.response_data) return normalizeDuplicateEnvelope(env, err?.response?.status);
    throw err;
  }
};

/**
 * Raise a duplicate-player flag. Privileged analysts create the player
 * immediately with this (spec §9); everyone else gets HTTP 400.
 * @param {object} p
 * @param {object} p.body  { user_data, analyst_id, potential_duplicates_data }
 * @param {string} p.token
 * @returns {Promise<{user_id: number|null, raw: object}>}
 */
export const raiseDuplicatePlayerFlag = async ({ body, token }) => {
  try {
    const { data } = await axios.post(
      `${MAIN_API}raise_duplicate_player_flag/`,
      {
        ...body,
        // Spec §9 — the matches are now sent as { id, fuzz_ratio } pairs.
        potential_duplicates_data: toPotentialDuplicates(body?.potential_duplicates_data),
      },
      {
        headers: {
          Authorization: "ANALYST " + token,
          "Content-Type": "application/json",
        },
      }
    );
    const parsed = readResponseData(data);
    return { user_id: parsed?.user_id ?? parsed?.id ?? null, raw: parsed };
  } catch (err) {
    const env = err?.response?.data;
    if (env?.response_data) throw duplicateError(env, err?.response?.status);
    throw err;
  }
};

/**
 * Raise an Ask-Admin approval request (spec §10). Analyst Portal auth only.
 * The player is NOT created here.
 * @returns {Promise<{request_id, duplicate_type}>}
 */
export const requestDuplicatePlayerApproval = async ({ userData, duplicates, token }) => {
  const { data } = await axios.post(
    `${MAIN_API}request_duplicate_player_approval/`,
    { user_data: userData, potential_duplicates_data: toPotentialDuplicates(duplicates) },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return data?.response_data ?? {};
};

/** Admin / Privileged approval inbox (spec §11). 403 for everyone else. */
export const listDuplicatePlayerApprovalRequests = async ({ token }) => {
  const { data } = await axios.get(
    `${MAIN_API}list_duplicate_player_approval_requests/`,
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return Array.isArray(data?.response_data) ? data.response_data : [];
};

/** Approve / reject a pending request (spec §13). */
export const resolveDuplicatePlayerApprovalRequest = async ({ requestId, approve, token }) => {
  const { data } = await axios.post(
    `${MAIN_API}resolve_duplicate_player_approval_request/`,
    { request_id: requestId, approve: !!approve },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return data?.response_data ?? {};
};

/**
 * Read-only duplicate-flags report between two dates. SAME_COUNTRY flags are
 * overridden at source (no approval queue), so they are surfaced here instead.
 * @param {object} p
 * @param {string} p.startDate  ISO / YYYY-MM-DD
 * @param {string} p.endDate
 * @param {string} [p.duplicateType]  e.g. "SAME_COUNTRY"
 * @param {string} p.token
 * @returns {Promise<Array>}
 */
export const duplicateFlagsBetween = async ({ startDate, endDate, duplicateType, token }) => {
  const { data } = await axios.post(
    `${MAIN_API}players/duplicate_flags_between/`,
    {
      start_date: startDate,
      end_date: endDate,
      ...(duplicateType ? { duplicate_type: duplicateType } : {}),
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  const rd = data?.response_data;
  return Array.isArray(rd) ? rd : Array.isArray(rd?.data) ? rd.data : [];
};

/**
 * Add searched players to a team.
 * Spec §14 documents a plain `response_data: true|false`; the encrypted object
 * shape is still accepted. Only an explicit `false` counts as a failure.
 * @param {object} p
 * @param {string|number} p.teamId
 * @param {Array<number>} p.userIds
 * @param {string} p.token
 * @returns {Promise<boolean>}
 */
export const addPlayersToTeam = async ({ teamId, userIds, organizerId, token }) => {
  const { data } = await axios.post(
    `${MAIN_API}updateTeam/`,
    {
      team_id: teamId,
      user_ids: userIds,
      hashed_required: true,
      ...(organizerId ? { organizer_id: organizerId } : {}),
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return readResponseData(data) !== false;
};

/**
 * Port player(s) to an organizer. Plain JSON response.
 * organizer_id is optional — when absent the backend resolves it from the token.
 * @param {object} p
 * @param {Array<number>} p.playerIds
 * @param {string|number} [p.organizerId]
 * @param {string} p.token
 */
export const portPlayerToOrganizer = async ({ playerIds, organizerId, token }) => {
  const { data } = await axios.post(
    `${MAIN_API}port_entity/`,
    {
      port_type: "player",
      port_list: playerIds,
      ...(organizerId ? { organizer_list: [organizerId] } : {}),
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return data;
};

/**
 * Bulk create/update lineup mappings (plain JSON, NOT encrypted). See spec §2.
 *
 * @param {object} p
 * @param {Array<object>} p.mappings  array of mapping objects
 * @param {string|number} [p.organizerId]  sent as the organizer_id query param
 *   (the body is an array, so it can't carry the field)
 * @param {string} p.token
 */
export const saveLineupBulk = async ({ mappings, organizerId, token }) => {
  const { data } = await axios.post(
    `${SSENSE_URL}create_user_match_mapping_bulk/`,
    { organizer_id: organizerId, data: mappings || [] },
    {
      // params: organizerId ? { organizer_id: organizerId } : {},
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return data;
};

export const getMatchById = async ({ match_id, token }) => {
  try {
    const { data } = await axios({
      method: "GET",
      url: `${MAIN_API}matchById/${match_id}?hashed_required=true`,
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    });
    return decryptResponse(data?.response_data, true);
  } catch (error) {
    throw error;
  }
};

export const updateEventLog = async ({
  matchId,
  analystId,
  eventLog,
  token,
}) => {
  try {
    const res = await axios.post(
      `${MAIN_API}update_analysis_event_log/`,
      {
        match_id: parseInt(matchId),
        analyst_id: parseInt(analystId),
        event: eventLog,
      },
      {
        headers: {
          Authorization: `ANALYST ${token}`,
        },
      }
    );
    return res.response_data;
  } catch (err) {
    console.error(err);
  }
};

export const updateMatchStatus = async ({
  match_id,
  match_status,
  token,
  reviewer_id,
}) => {
  const response = await axios.post(
    `${MAIN_API}update_match_status/`,
    {
      match_id,
      match_status,
      reviewer_id,
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const getMatchLiveData = async ({ matchId }) => {
  try {
    const res = await axios.post(`${SSENSE_URL}get_live_tag_data/`, {
      match_id: parseInt(matchId),
    });
    return res?.data?.response_data;
  } catch (err) {
    console.error("Error fetching live data:", err);
    return null;
  }
};

export const PushMatchLiveData = async ({
  matchId,
  teamAStats,
  teamBStats,
}) => {
  try {
    const payload = {
      match_id: parseInt(matchId),
      data: {
        team_a_details: teamAStats,
        team_b_details: teamBStats,
      },
    };

    const res = await axios.post(`${SSENSE_URL}push_live_tag_data/`, payload);
    return res?.data;
  } catch (err) {
    console.error("Error pushing live data:", err);
    return null;
  }
};

export const updateLiveTaggingStatus = async ({
  match_id,
  live_tagging_status,
  token,
}) => {
  const response = await axios.post(
    `${MAIN_API}update_live_tagging_status/`,
    {
      match_id,
      live_tagging_status,
    },
    {
      headers: {
        Authorization: "ANALYST " + token,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const fetchMatchStringById = async (match_id, analyst_id, token) => {
  try {
    const data = {
      match_id,
      analyst_id,
    };

    const response = await axios.post(
      `${SSENSE_URL}get_match_video_string/v2/`,
      data,
      {
        headers: {
          Authorization: `ANALYST ${token}`,
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error("Error fetching match string:", error);
    return null;
  }
};

export const runTechQC = async (event_strings, token, match_id) => {
  try {
    const data = {
      event_strings,
      match_id,
    };

    const response = await axios.post(`${SSENSE_URL}perform_tech_qc/`, data, {
      headers: {
        Authorization: `ANALYST ${token}`,
      },
    });

    return response?.data;
  } catch (error) {
    console.error("Error fetching match string:", error);
    return null;
  }
};

export const pushCompletedTechQCString = async (
  match_id,
  analyst_id,
  token,
  match_string,
  force_update //only enabled in techQC
) => {
  const data = {
    match_id,
    analyst_id,
    match_string,
    force_update,
  };

  try {
    const response = await axios.post(
      `${SSENSE_URL}push_match_video_string/v2/`,
      data,
      {
        headers: {
          Authorization: "ANALYST " + token,
        },
      }
    );

    if (response.status === 200) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: "Unexpected status code" };
    }
  } catch (error) {
    console.error("API Error:", error);
    return { success: false, error: error.message };
  }
};

